import { Button, Icon, Input, Badge, Tooltip } from '@aidkitorg/component-library';
import { SupportedLanguage, SupportedLanguages, LanguageNames, APPLICANT_FACING, BACKEND_ONLY, Audience } from '@aidkitorg/i18n/lib';
import Papa from 'papaparse';
import * as React from 'react';
import { usePost } from '../API';
import { useDebouncedMemo } from '../Hooks/Debounce';

type SortOption = 'alphabetical' | 'last_modified' | 'needs_review' | 'applicant_facing_first';
type FilterOption = 'all' | 'needs_review' | 'human_reviewed';
type AudienceFilterOption = 'all' | typeof APPLICANT_FACING | typeof BACKEND_ONLY;
type TranslationEdits = Partial<Record<SupportedLanguage, Record<string, string>>>;
type TranslationCache = Partial<Record<SupportedLanguage, TranslationsResponse>>;
type AudienceOverrides = Record<string, Audience>;

const DEFAULT_SORT: SortOption = 'needs_review';
const DEFAULT_FILTER: FilterOption = 'needs_review';
const STORAGE_KEY = 'translation-edits';
const AUDIENCE_OVERRIDES_KEY = 'translation-audience-overrides';

const CONFIRM_MESSAGES = {
  discardLanguage: (count: number, lang: string) =>
    `Discard all ${count} ${lang} changes?`,
  discardAudienceOverrides: (count: number) =>
    `Discard all ${count} audience override${count !== 1 ? 's' : ''}?`,
  discardAll: (count: number) =>
    `Discard all ${count} unsaved changes?`,
};

interface Translation {
  lang: string;
  key: string;
  ai_translation: string;
  human_translation?: string;
  last_modified_by: string;
  last_modified: string;
  needs_review?: boolean;
  audience?: typeof APPLICANT_FACING | typeof BACKEND_ONLY;
}

interface TranslationStats {
  total: number;
  ai_only: number;
  human_reviewed: number;
  coverage_percent: number;
}

interface TranslationsResponse {
  translations: Translation[];
  lang: string;
  stats: TranslationStats;
}

const createTranslationMap = (translations?: Translation[]): Record<string, Translation> => {
  if (!translations) return {};
  return translations.reduce((map, t) => ({ ...map, [t.key]: t }), {});
};

interface TranslationControlsProps {
  baseLang: SupportedLanguage | '';
  setBaseLang: (lang: SupportedLanguage | '') => void;
  translationLang: SupportedLanguage;
  setTranslationLang: (lang: SupportedLanguage) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  sortBy: SortOption;
  setSortBy: (sort: SortOption) => void;
  filterBy: FilterOption;
  setFilterBy: (filter: FilterOption) => void;
  audienceFilter: AudienceFilterOption;
  setTagFilter: (tag: AudienceFilterOption) => void;
  baseError: string | null;
  translationError: string | null;
  translationStats: TranslationStats | null;
}

const TranslationControls = React.memo<TranslationControlsProps>(({
  baseLang,
  setBaseLang,
  translationLang,
  setTranslationLang,
  searchText,
  setSearchText,
  sortBy,
  setSortBy,
  filterBy,
  setFilterBy,
  audienceFilter,
  setTagFilter,
  baseError,
  translationError,
  translationStats
}) => {
  return (
    <>
      {/* Language Selection */}
      <div className="flex gap-4 mb-6">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Base Language (for comparison)
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            value={baseLang}
            onChange={(e) => setBaseLang(e.target.value as SupportedLanguage | '')}
          >
            <option value="">None</option>
            {SupportedLanguages.filter(lang => lang !== translationLang).map(lang => (
              <option key={lang} value={lang}>
                {LanguageNames[lang]}
              </option>
            ))}
          </select>
        </div>

        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Translation Language
          </label>
          <select
            className="w-full border border-gray-300 rounded-md px-3 py-2"
            value={translationLang}
            onChange={(e) => setTranslationLang(e.target.value as SupportedLanguage)}
          >
            {SupportedLanguages.map(lang => (
              <option key={lang} value={lang}>
                {LanguageNames[lang]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error Banners */}
      {baseError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-red-800">
          {baseError} (base language). Please try again.
        </div>
      )}
      {translationError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-4 text-red-800">
          {translationError}. Please try again.
        </div>
      )}

      {translationStats && (
        <div className="flex gap-4 mb-4">
          <Badge variant="info">
            Total: {translationStats.total}
          </Badge>
          <Badge variant="warning">
            AI Only: {translationStats.ai_only}
          </Badge>
          <Badge variant="success">
            Human Reviewed: {translationStats.human_reviewed} ({translationStats.coverage_percent}%)
          </Badge>
        </div>
      )}

      {/* Search, Filter, and Sort */}
      <div className="flex items-end gap-4 mb-4">
        <div className="flex-1">
          <Input
            label="Search"
            placeholder="Search by key or text..."
            value={searchText}
            onChange={(e) => setSearchText(e.currentTarget.value)}
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Status
          </label>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {([['all', 'All'], ['needs_review', 'Needs Review'], ['human_reviewed', 'Reviewed']] as const).map(([value, label], i) => (
              <button
                key={value}
                onClick={() => setFilterBy(value)}
                className={[
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  i > 0 ? 'border-l border-gray-300' : '',
                  filterBy === value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Audience
          </label>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {([['all', 'All'], [APPLICANT_FACING, 'Applicant Facing'], [BACKEND_ONLY, 'Backend Only']] as const).map(([value, label], i) => (
              <button
                key={value}
                onClick={() => setTagFilter(value)}
                className={[
                  'px-3 py-1.5 text-sm font-medium transition-colors',
                  i > 0 ? 'border-l border-gray-300' : '',
                  audienceFilter === value
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-50',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
            Sort By
          </label>
          <select
            className="border border-gray-300 rounded-md px-3 py-1.5 text-sm"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
          >
            <option value="alphabetical">Alphabetical</option>
            <option value="last_modified">Last Modified</option>
            <option value="needs_review">Needs Review</option>
            <option value="applicant_facing_first">Applicant Facing First</option>
          </select>
        </div>
      </div>
    </>
  );
});

interface TranslationsTableProps {
  baseLang: SupportedLanguage | '';
  translationLang: SupportedLanguage;
  filteredKeys: string[];
  baseTranslationsMap: Record<string, Translation>;
  translationMap: Record<string, Translation>;
  editingTranslations: TranslationEdits;
  translationError: string | null;
  onRowClick: (key: string) => void;
  onApproveAI: (key: string, aiTranslation: string) => void;
}

const TranslationsTable = React.memo<TranslationsTableProps>(({
  baseLang,
  translationLang,
  filteredKeys,
  baseTranslationsMap,
  translationMap,
  editingTranslations,
  translationError,
  onRowClick,
  onApproveAI
}) => {
  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/5">
              Key
            </th>
            {baseLang && (
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                {LanguageNames[baseLang]}
              </th>
            )}
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
              {LanguageNames[translationLang]} (AI)
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
              {LanguageNames[translationLang]} (Human)
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              Status
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              Audience
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredKeys.map((key) => {
            const base = baseTranslationsMap[key];
            const trans = translationMap[key];
            const localEdit = editingTranslations[translationLang]?.[key];
            const displayValue = localEdit ?? trans?.human_translation;

            return (
              <tr
                key={key}
                className="hover:bg-gray-50 cursor-pointer"
                onClick={() => onRowClick(key)}
              >
                <td className="px-4 py-3 text-xs text-gray-900 font-mono max-w-0">
                  <Tooltip text={key} side="top" asChild>
                    <span className="truncate block">{key}</span>
                  </Tooltip>
                </td>
                {baseLang && (
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Tooltip text={base?.human_translation ?? base?.ai_translation ?? '-'} side="top" asChild>
                        <span className="truncate">{base?.human_translation ?? base?.ai_translation ?? '-'}</span>
                      </Tooltip>
                      {base?.human_translation ? (
                        <Icon name="CheckCircleIcon" size="sm" className="text-green-600 flex-shrink-0" role="informative" title="Human reviewed" />
                      ) : base?.ai_translation ? (
                        <Icon name="SparklesIcon" size="sm" className="text-blue-500 flex-shrink-0" role="informative" title="AI translation" />
                      ) : null}
                    </div>
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-gray-700 max-w-0">
                  <Tooltip text={trans?.ai_translation ?? '-'} side="top" asChild>
                    <span className="truncate block">{trans?.ai_translation ?? '-'}</span>
                  </Tooltip>
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 max-w-0">
                  <Tooltip text={displayValue ?? '-'} side="top" asChild>
                    <span className="truncate block">{displayValue ?? <span className="text-gray-400">-</span>}</span>
                  </Tooltip>
                </td>
                <td className="px-4 py-3">
                  {localEdit ? (
                    <Badge variant="info">Edited</Badge>
                  ) : trans?.needs_review ? (
                    <Badge variant="warning">Needs Review</Badge>
                  ) : trans?.human_translation ? (
                    <Badge variant="success">Reviewed</Badge>
                  ) : trans?.ai_translation ? (
                    <Badge variant="warning">AI Only</Badge>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {trans?.audience === APPLICANT_FACING ? (
                    <Badge variant="info">Applicant</Badge>
                  ) : trans?.audience === BACKEND_ONLY ? (
                    <Badge variant="neutral">Backend</Badge>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  {trans?.ai_translation && !trans?.human_translation && !localEdit && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onApproveAI(key, trans.ai_translation)}
                    >
                      Approve AI
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {filteredKeys.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          {translationError ? 'Unable to load translations.' : 'No translations found.'}
        </div>
      )}
    </div>
  );
});

interface EditTranslationModalProps {
  viewingKey: string | null;
  baseLang: SupportedLanguage | '';
  translationLang: SupportedLanguage;
  baseTranslationsMap: Record<string, Translation>;
  translationMap: Record<string, Translation>;
  updatedHumanTranslationValue: string;
  pendingAudience: Audience | null;
  onClose: () => void;
  onUpdateValue: (value: string) => void;
  onUpdateAudience: (audience: Audience) => void;
  onSave: () => void;
}

const EditTranslationModal = React.memo<EditTranslationModalProps>(({
  viewingKey,
  baseLang,
  translationLang,
  baseTranslationsMap,
  translationMap,
  updatedHumanTranslationValue,
  pendingAudience,
  onClose,
  onUpdateValue,
  onUpdateAudience,
  onSave
}) => {
  if (!viewingKey) return null;

  const baseTranslation = baseTranslationsMap[viewingKey];
  const translation = translationMap[viewingKey];
  const isBaseHumanReviewed = baseTranslation?.human_translation !== undefined;

  const savedAudience = translation?.audience ?? APPLICANT_FACING;
  const effectiveAudience = pendingAudience ?? savedAudience;
  const isAudienceOverridden = pendingAudience !== null && pendingAudience !== savedAudience;
  const overrideTo = effectiveAudience === APPLICANT_FACING ? BACKEND_ONLY : APPLICANT_FACING;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold">Translation Details</h2>
              <p className="text-sm text-gray-600 font-mono mt-1">{viewingKey}</p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <Icon name="XMarkIcon" size="md" />
            </button>
          </div>

          <div className="space-y-4">
            {/* Show base language translation if selected */}
            {baseLang && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                  {LanguageNames[baseLang]} Translation
                  {baseTranslation && (
                    <span className="text-xs font-normal">
                      {isBaseHumanReviewed ? (
                        <Badge variant="success">Human Reviewed</Badge>
                      ) : (
                        <Badge variant="info">AI Only</Badge>
                      )}
                    </span>
                  )}
                </label>
                <div className={`bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm ${isBaseHumanReviewed ? 'text-gray-700' : 'text-gray-500 italic'}`}>
                  {baseTranslation?.human_translation ?? baseTranslation?.ai_translation ?? '-'}
                </div>
              </div>
            )}

            {/* AI Translation */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {LanguageNames[translationLang]} (AI Translation)
              </label>
              <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700">
                {translation?.ai_translation ?? '-'}
              </div>
            </div>

            {/* Human Translation (Read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current {LanguageNames[translationLang]} (Human Translation)
              </label>
              <div className="bg-gray-50 border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-700">
                {translation?.human_translation || <span className="text-gray-400 italic">Not set</span>}
              </div>
            </div>

            {/* Updated Human Translation (Editable) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Updated {LanguageNames[translationLang]} (Human Translation)
              </label>
              <textarea
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={4}
                value={updatedHumanTranslationValue}
                onChange={(e) => onUpdateValue(e.currentTarget.value)}
                placeholder="Enter human-reviewed translation..."
              />
            </div>

            {/* Audience Override */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Audience</label>
              <div className="flex items-center gap-3">
                <Badge variant={effectiveAudience === APPLICANT_FACING ? 'info' : 'neutral'}>
                  {effectiveAudience === APPLICANT_FACING ? 'Applicant Facing' : 'Backend Only'}
                </Badge>
                {isAudienceOverridden ? (
                  <button
                    type="button"
                    onClick={() => onUpdateAudience(savedAudience)}
                    className="text-xs text-orange-600 hover:text-orange-800 underline"
                  >
                    Undo override
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onUpdateAudience(overrideTo)}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Override to {overrideTo === APPLICANT_FACING ? 'Applicant Facing' : 'Backend Only'}
                  </button>
                )}
              </div>
            </div>

            {/* Metadata */}
            {translation && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <div>
                      {translation.needs_review ? (
                        <Badge variant="warning">Needs Review</Badge>
                      ) : (
                        <Badge variant="success">Reviewed</Badge>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Last Modified By
                    </label>
                    <div className="text-sm text-gray-700">
                      {translation.last_modified_by}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Modified
                  </label>
                  <div className="text-sm text-gray-700">
                    {new Date(translation.last_modified).toLocaleString()}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button
              variant="secondary"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onSave}
            >
              Save (Local)
            </Button>
          </div>

          <div className="mt-2 text-xs text-gray-500 text-right">
            Changes are saved locally. Use "Save All" to push to database.
          </div>
        </div>
      </div>
    </div>
  );
});

function applyAudienceFilter<T extends { audience?: typeof APPLICANT_FACING | typeof BACKEND_ONLY }>(
  items: T[],
  filter: { applicantFacing: boolean; backend: boolean }
): T[] {
  if (filter.applicantFacing && filter.backend) return items;
  return items.filter(t =>
    (filter.applicantFacing && t.audience === APPLICANT_FACING) ||
    (filter.backend && t.audience === BACKEND_ONLY)
  );
}

function generateCSV(
  keys: string[],
  baseLangs: Array<{ lang: SupportedLanguage; map: Record<string, Translation> }>,
  targetMap: Record<string, Translation>,
  targetLang: SupportedLanguage,
): string {
  return Papa.unparse(keys.map(key => ({
    key,
    ...Object.fromEntries(baseLangs.map(({ lang, map }) => {
      const t = map[key];
      return [`base_${lang}`, t?.human_translation ?? t?.ai_translation ?? ''];
    })),
    [`${targetLang}_ai`]: targetMap[key]?.ai_translation ?? '',
    [`${targetLang}_human`]: targetMap[key]?.human_translation ?? '',
  })));
}

interface ParsedCSV {
  detectedLang: SupportedLanguage | null;
  updates: Array<{ key: string; human_translation: string }>;
  rowsEmpty: number;
}

interface ImportTranslationsModalProps {
  onClose: () => void;
  loadedTranslations: TranslationCache;
  onImportComplete: (lang: SupportedLanguage) => Promise<void>;
  getTranslations: (params: { lang: string }) => Promise<unknown>;
}

function ImportTranslationsModal({
  onClose,
  loadedTranslations,
  onImportComplete,
  getTranslations,
}: ImportTranslationsModalProps) {
  const bulkUpdate = usePost('/api/translations/bulk-update');

  const [selectedLang, setSelectedLang] = React.useState<SupportedLanguage>('es');
  const [parsedResult, setParsedResult] = React.useState<ParsedCSV | null>(null);
  const [isImporting, setIsImporting] = React.useState(false);
  const [importResult, setImportResult] = React.useState<{ updated: number; skipped: number } | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [localCache, setLocalCache] = React.useState<TranslationCache>(loadedTranslations);
  const [isLoadingLang, setIsLoadingLang] = React.useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const fetchedLangs = React.useRef(new Set(Object.keys(loadedTranslations)));

  // Load selected language into local cache for unknown key validation
  React.useEffect(() => {
    if (fetchedLangs.current.has(selectedLang)) return;
    fetchedLangs.current.add(selectedLang);
    setIsLoadingLang(true);
    getTranslations({ lang: selectedLang })
      .then(response => setLocalCache(prev => ({ ...prev, [selectedLang]: response as TranslationsResponse })))
      .catch(err => console.error(`Failed to load ${selectedLang} for import validation:`, err))
      .finally(() => setIsLoadingLang(false));
  }, [selectedLang, getTranslations]);

  const validatedResult = React.useMemo(() => {
    if (!parsedResult) return null;
    const langMismatch = parsedResult.detectedLang !== null && parsedResult.detectedLang !== selectedLang;
    const langData = localCache[selectedLang];
    const knownKeys = langData ? new Set(langData.translations.map(t => t.key)) : null;
    const unknownKeys = knownKeys
      ? parsedResult.updates.filter(u => !knownKeys.has(u.key)).map(u => u.key)
      : [];
    return { ...parsedResult, langMismatch, unknownKeys };
  }, [parsedResult, selectedLang, localCache]);

  const handleLangChange = (lang: SupportedLanguage) => {
    setSelectedLang(lang);
    setParsedResult(null);
    setError(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setParsedResult(null);
    setError(null);
    setImportResult(null);

    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const headers = results.meta.fields ?? [];

        if (!headers.includes('key')) {
          setError('CSV is missing a required "key" column.');
          setParsedResult(null);
          return;
        }

        const humanColHeader = headers.find(h =>
          h.endsWith('_human') && SupportedLanguages.includes(h.replace(/_human$/, '') as SupportedLanguage)
        );
        if (!humanColHeader) {
          setError('CSV is missing a valid {lang}_human column. Make sure you\'re uploading an export from this tool.');
          setParsedResult(null);
          return;
        }

        const detectedLang = humanColHeader.replace(/_human$/, '') as SupportedLanguage;
        const updates: Array<{ key: string; human_translation: string }> = [];
        let rowsEmpty = 0;

        for (const row of results.data) {
          const key = row['key']?.trim();
          if (!key) continue;
          const humanTranslation = row[humanColHeader]?.trim() ?? '';
          if (!humanTranslation) {
            rowsEmpty++;
            const aiTranslation = row[`${detectedLang}_ai`]?.trim();
            if (aiTranslation) updates.push({ key, human_translation: aiTranslation });
          } else {
            updates.push({ key, human_translation: humanTranslation });
          }
        }

        setParsedResult({ detectedLang, updates, rowsEmpty });
      },
      error: (err) => {
        setError(`Failed to parse CSV: ${err.message}`);
        setParsedResult(null);
      },
    });
  };

  const { previewRows, rowsToImport, unchangedCount, changedUpdates } = React.useMemo(() => {
    const empty = {
      previewRows: [] as Array<{ key: string; current: string | undefined; updated: string }>,
      changedUpdates: [] as Array<{ key: string; human_translation: string }>,
      rowsToImport: 0,
      unchangedCount: 0,
    };
    if (!validatedResult || validatedResult.langMismatch) return empty;
    const unknownKeySet = new Set(validatedResult.unknownKeys);
    const currentMap = createTranslationMap(localCache[selectedLang]?.translations);
    const known = validatedResult.updates.filter(u => !unknownKeySet.has(u.key));
    const changed = known.filter(u => u.human_translation !== currentMap[u.key]?.human_translation);
    return {
      changedUpdates: known,
      rowsToImport: known.length,
      unchangedCount: known.length - changed.length,
      previewRows: changed.map(u => ({ key: u.key, current: currentMap[u.key]?.human_translation, updated: u.human_translation })),
    };
  }, [validatedResult, selectedLang, localCache]);

  const handleImport = async () => {
    if (!validatedResult || validatedResult.langMismatch) return;
    setIsImporting(true);
    setError(null);
    try {
      const result = await bulkUpdate({ lang: selectedLang, updates: changedUpdates }) as { updated: number; skipped: number };
      await onImportComplete(selectedLang);
      setImportResult(result);
    } catch (err) {
      console.error('Failed to import translations:', err);
      setError('Import failed. Please try again.');
    } finally {
      setIsImporting(false);
    }
  };

  const canImport = validatedResult !== null
    && !validatedResult.langMismatch
    && rowsToImport > 0
    && !isImporting
    && !isLoadingLang
    && importResult === null
    && localCache[selectedLang] !== undefined;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-bold">Import Translations</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <Icon name="XMarkIcon" size="md" />
            </button>
          </div>

          {importResult ? (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-md p-4">
                <p className="text-green-800 font-medium">Import complete</p>
                <p className="text-green-700 text-sm mt-1">
                  {importResult.updated} translation{importResult.updated !== 1 ? 's' : ''} updated
                  {importResult.skipped > 0 && `, ${importResult.skipped} skipped`}
                </p>
              </div>
              <div className="flex justify-end">
                <Button variant="primary" onClick={onClose}>Done</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Language selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Translation Language
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2"
                  value={selectedLang}
                  onChange={(e) => handleLangChange(e.target.value as SupportedLanguage)}
                >
                  {SupportedLanguages.map(lang => (
                    <option key={lang} value={lang}>{LanguageNames[lang]}</option>
                  ))}
                </select>
                {isLoadingLang && (
                  <p className="text-xs text-gray-500 mt-1">Loading language data for validation...</p>
                )}
              </div>

              {/* File upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">CSV File</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
              </div>

              {/* Validation + preview */}
              {validatedResult && (
                <div className="space-y-3">
                  {validatedResult.langMismatch && (
                    <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-800">
                      Language mismatch: CSV contains <strong>{validatedResult.detectedLang ? LanguageNames[validatedResult.detectedLang] : validatedResult.detectedLang}</strong> translations
                      but <strong>{LanguageNames[selectedLang]}</strong> is selected. Update the language selector to match.
                    </div>
                  )}

                  {validatedResult.unknownKeys.length > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                      <p className="font-medium">{validatedResult.unknownKeys.length} unknown key{validatedResult.unknownKeys.length !== 1 ? 's' : ''} will be skipped</p>
                      <p className="text-xs mt-1 text-yellow-700">These keys don't exist in the database for {LanguageNames[selectedLang]}.</p>
                    </div>
                  )}

                  {!validatedResult.langMismatch && (
                    <div className="space-y-2">
                      <div className="text-sm text-gray-600">
                        {rowsToImport} row{rowsToImport !== 1 ? 's' : ''} will be imported
                        {validatedResult.rowsEmpty > 0 && `, ${validatedResult.rowsEmpty} empty`}
                        {validatedResult.unknownKeys.length > 0 && `, ${validatedResult.unknownKeys.length} skipped (unknown key)`}
                        {unchangedCount > 0 && `, ${unchangedCount} confirmed as-is`}
                      </div>
                      {validatedResult.rowsEmpty > 0 && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                          Note: Skipped rows will still be marked as human reviewed.
                        </div>
                      )}
                      {rowsToImport === 0 && (validatedResult.rowsEmpty > 0 || validatedResult.unknownKeys.length > 0) && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 text-sm text-yellow-800">
                          No updates to apply — all rows are either empty or have unknown keys.
                        </div>
                      )}
                      {previewRows.length > 0 && (
                        <div className="max-h-60 overflow-y-auto bg-gray-50 rounded p-3 space-y-3">
                          {previewRows.map(({ key, current, updated }) => (
                            <div key={key} className="border-b border-gray-200 pb-3 last:border-b-0">
                              <div className="text-xs font-mono text-gray-600 mb-2">{key}</div>
                              <div className="space-y-1">
                                <div className="flex items-start gap-2">
                                  <span className="text-xs font-medium text-gray-500 flex-shrink-0">Current:</span>
                                  <span className="text-xs text-gray-700 italic">
                                    {current || <span className="text-gray-400">Not set</span>}
                                  </span>
                                </div>
                                <div className="flex items-start gap-2">
                                  <span className="text-xs font-medium text-green-600 flex-shrink-0">Updated:</span>
                                  <span className="text-xs text-gray-900 font-medium">{updated}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {error && (
                <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="secondary" onClick={onClose} disabled={isImporting}>Cancel</Button>
                <Button variant="primary" onClick={handleImport} disabled={!canImport}>
                  {isImporting ? 'Importing...' : 'Import'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface ExportTranslationsModalProps {
  onClose: () => void;
  loadedTranslations: TranslationCache;
  getTranslations: (params: { lang: string }) => Promise<unknown>;
}

function ExportTranslationsModal({
  onClose,
  loadedTranslations,
  getTranslations,
}: ExportTranslationsModalProps) {
  const [targetLang, setTargetLang] = React.useState<SupportedLanguage>('es');
  const [baseLangs, setBaseLangs] = React.useState<SupportedLanguage[]>(['en']);
  const [audienceFilter, setAudienceFilter] = React.useState({ applicantFacing: true, backend: true });
  const [needsReviewOnly, setNeedsReviewOnly] = React.useState(true);
  const [localCache, setLocalCache] = React.useState<TranslationCache>(loadedTranslations);
  const [isLoading, setIsLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const toggleBaseLang = React.useCallback((lang: SupportedLanguage, checked: boolean) => {
    setBaseLangs(prev => checked ? [...prev, lang] : prev.filter(l => l !== lang));
  }, []);

  const previewCount = React.useMemo(() => {
    const targetData = localCache[targetLang];
    if (!targetData) return null;
    let translations = applyAudienceFilter(targetData.translations, audienceFilter);
    if (needsReviewOnly) {
      translations = translations.filter(t => t.needs_review);
    }
    return translations.length;
  }, [localCache, targetLang, audienceFilter, needsReviewOnly]);

  const isValid = baseLangs.length > 0 && (audienceFilter.applicantFacing || audienceFilter.backend);

  const handleExport = React.useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const langsNeeded = Array.from(new Set<SupportedLanguage>([...baseLangs, targetLang]));
      const fetchedCache: TranslationCache = { ...localCache };
      await Promise.all(
        langsNeeded
          .filter(lang => !fetchedCache[lang])
          .map(async lang => {
            const response = await getTranslations({ lang });
            fetchedCache[lang] = response as TranslationsResponse;
          })
      );
      setLocalCache(fetchedCache);

      const targetMap = createTranslationMap(fetchedCache[targetLang]?.translations);
      const baseLangMaps = baseLangs.map(lang => ({
        lang,
        map: createTranslationMap(fetchedCache[lang]?.translations),
      }));

      const allTranslations = Object.values(targetMap);
      const filteredTranslations = applyAudienceFilter(allTranslations, audienceFilter)
        .filter(t => !needsReviewOnly || t.needs_review);
      const keys = filteredTranslations.map(t => t.key).sort((a, b) => a.localeCompare(b));

      const csv = generateCSV(keys, baseLangMaps, targetMap, targetLang);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `translations_${targetLang}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      onClose();
    } catch (err) {
      console.error('Failed to export translations:', err);
      setError('Failed to load translations for export. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [baseLangs, targetLang, audienceFilter, needsReviewOnly, localCache, getTranslations, onClose]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <h2 className="text-xl font-bold">Export Translations</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <Icon name="XMarkIcon" size="md" />
            </button>
          </div>

          <div className="space-y-5">
            {/* Translation Language */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Translation Language
              </label>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={targetLang}
                onChange={(e) => setTargetLang(e.target.value as SupportedLanguage)}
              >
                {SupportedLanguages.map(lang => (
                  <option key={lang} value={lang}>{LanguageNames[lang]}</option>
                ))}
              </select>
            </div>

            {/* Base Languages */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Base Languages <span className="text-gray-500 font-normal text-xs">(at least 1 required)</span>
              </label>
              <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-0.5">
                {SupportedLanguages.map(lang => (
                  <label key={lang} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={baseLangs.includes(lang)}
                      onChange={(e) => toggleBaseLang(lang, e.target.checked)}
                      className="rounded"
                    />
                    <span className="text-sm">{LanguageNames[lang]}</span>
                  </label>
                ))}
              </div>
              {baseLangs.length === 0 && (
                <p className="text-xs text-red-500 mt-1">Select at least one base language.</p>
              )}
            </div>

            {/* Audience */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Audience</label>
              <div className="flex gap-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={audienceFilter.applicantFacing}
                    onChange={(e) => setAudienceFilter(prev => ({ ...prev, applicantFacing: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Applicant Facing</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={audienceFilter.backend}
                    onChange={(e) => setAudienceFilter(prev => ({ ...prev, backend: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Backend</span>
                </label>
              </div>
              {!audienceFilter.applicantFacing && !audienceFilter.backend && (
                <p className="text-xs text-red-500 mt-1">Select at least one audience.</p>
              )}
            </div>

            {/* Needs Review Toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={needsReviewOnly}
                onChange={(e) => setNeedsReviewOnly(e.target.checked)}
                className="rounded"
              />
              <span className="text-sm font-medium text-gray-700">Only include translations that need review</span>
            </label>

            {/* Preview */}
            {previewCount !== null && (
              <div className="text-sm text-gray-600 bg-gray-50 rounded-md px-3 py-2">
                {previewCount} row{previewCount !== 1 ? 's' : ''} will be exported
              </div>
            )}

            {error && (
              <div className="text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">{error}</div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="secondary" onClick={onClose} disabled={isLoading}>Cancel</Button>
            <Button variant="primary" onClick={handleExport} disabled={!isValid || isLoading}>
              {isLoading ? 'Loading...' : 'Export CSV'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface SaveAllModalProps {
  editingTranslations: TranslationEdits;
  audienceOverrides: AudienceOverrides;
  allTranslationsData: TranslationCache;
  onClose: () => void;
  onSaveLanguage: (lang: SupportedLanguage) => Promise<void>;
  onSaveAll: () => Promise<void>;
  onSaveAudienceOverrides: () => Promise<void>;
  onDiscardAudienceOverrides: () => void;
  onDiscardLanguage: (lang: SupportedLanguage) => void;
  onDiscardAll: () => void;
  isSaving: boolean;
}

const SaveAllModal = React.memo<SaveAllModalProps>(({
  editingTranslations,
  audienceOverrides,
  allTranslationsData,
  onClose,
  onSaveLanguage,
  onSaveAll,
  onSaveAudienceOverrides,
  onDiscardAudienceOverrides,
  onDiscardLanguage,
  onDiscardAll,
  isSaving
}) => {
  const getCurrentHumanTranslation = (lang: SupportedLanguage, key: string): string | undefined => {
    const translation = allTranslationsData[lang]?.translations.find(t => t.key === key);
    return translation?.human_translation;
  };

  // Only include edits where the new value actually differs from the saved human_translation
  const languagesWithEdits = Object.entries(editingTranslations)
    .map(([lang, edits]) => ({
      lang: lang as SupportedLanguage,
      edits: Object.fromEntries(
        Object.entries(edits).filter(([key, newValue]) =>
          newValue !== getCurrentHumanTranslation(lang as SupportedLanguage, key)
        )
      ),
    }))
    .filter(({ edits }) => Object.keys(edits).length > 0);

  const totalEdits = languagesWithEdits.reduce((sum, { edits }) => sum + Object.keys(edits).length, 0);
  const audienceOverrideEntries = Object.entries(audienceOverrides);
  const totalUnsaved = totalEdits + audienceOverrideEntries.length;

  if (languagesWithEdits.length === 0 && audienceOverrideEntries.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h2 className="text-xl font-bold">Save Changes</h2>
              <p className="text-sm text-gray-600 mt-1">
                {totalEdits > 0 && `${totalEdits} unsaved translation${totalEdits !== 1 ? 's' : ''} across ${languagesWithEdits.length} language${languagesWithEdits.length !== 1 ? 's' : ''}`}
                {totalEdits > 0 && audienceOverrideEntries.length > 0 && ', '}
                {audienceOverrideEntries.length > 0 && `${audienceOverrideEntries.length} audience override${audienceOverrideEntries.length !== 1 ? 's' : ''}`}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isSaving}
            >
              <Icon name="XMarkIcon" size="md" />
            </button>
          </div>

          <div className="space-y-6">
            {languagesWithEdits.map(({ lang, edits }) => {
              const editCount = Object.keys(edits).length;
              return (
                <div key={lang} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <h3 className="font-semibold text-lg">{LanguageNames[lang]}</h3>
                      <p className="text-sm text-gray-600">{editCount} edited translation{editCount !== 1 ? 's' : ''}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => {
                          if (confirm(CONFIRM_MESSAGES.discardLanguage(editCount, LanguageNames[lang]))) {
                            onDiscardLanguage(lang);
                          }
                        }}
                        disabled={isSaving}
                      >
                        Discard
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => onSaveLanguage(lang)}
                        disabled={isSaving}
                      >
                        Save {LanguageNames[lang]}
                      </Button>
                    </div>
                  </div>

                  <div className="max-h-60 overflow-y-auto bg-gray-50 rounded p-3 space-y-3">
                    {Object.entries(edits).map(([key, newValue]) => {
                      const currentValue = getCurrentHumanTranslation(lang, key);
                      return (
                        <div key={key} className="border-b border-gray-200 pb-3 last:border-b-0">
                          <div className="text-xs font-mono text-gray-600 mb-2">{key}</div>
                          <div className="space-y-1">
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-gray-500 flex-shrink-0">Current:</span>
                              <span className="text-xs text-gray-700 italic">
                                {currentValue || <span className="text-gray-400">Not set</span>}
                              </span>
                            </div>
                            <div className="flex items-start gap-2">
                              <span className="text-xs font-medium text-green-600 flex-shrink-0">Updated:</span>
                              <span className="text-xs text-gray-900 font-medium">{newValue}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            {audienceOverrideEntries.length > 0 && (
              <div className="border border-orange-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <div>
                    <h3 className="font-semibold text-lg">Audience Overrides</h3>
                    <p className="text-sm text-gray-600">{audienceOverrideEntries.length} key{audienceOverrideEntries.length !== 1 ? 's' : ''} — applies to all languages</p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        if (confirm(CONFIRM_MESSAGES.discardAudienceOverrides(audienceOverrideEntries.length))) {
                          onDiscardAudienceOverrides();
                        }
                      }}
                      disabled={isSaving}
                    >
                      Discard
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={onSaveAudienceOverrides}
                      disabled={isSaving}
                    >
                      Save Audience Overrides
                    </Button>
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto bg-gray-50 rounded p-3 space-y-2">
                  {audienceOverrideEntries.map(([key, audience]) => (
                    <div key={key} className="border-b border-gray-200 pb-2 last:border-b-0 flex items-center justify-between">
                      <span className="text-xs font-mono text-gray-600">{key}</span>
                      <Badge variant={audience === APPLICANT_FACING ? 'info' : 'neutral'}>
                        {audience === APPLICANT_FACING ? 'Applicant Facing' : 'Backend Only'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between items-center mt-6 pt-4 border-t">
            <div className="flex gap-2">
              <Button
                variant="secondary"
                onClick={onClose}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  if (confirm(CONFIRM_MESSAGES.discardAll(totalUnsaved))) {
                    onDiscardAll();
                    onClose();
                  }
                }}
                disabled={isSaving}
                className="text-red-600 hover:text-red-700"
              >
                Discard All
              </Button>
            </div>
            <Button
              variant="primary"
              onClick={onSaveAll}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : `Save All (${totalUnsaved})`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export const TranslationOverview = () => {
  const getTranslations = usePost('/api/translations/get');
  const updateHumanTranslation = usePost('/api/translations/update');
  const updateAudience = usePost('/api/translations/update-audience');

  const [baseLang, setBaseLang] = React.useState<SupportedLanguage | ''>('en');
  const [translationLang, setTranslationLang] = React.useState<SupportedLanguage>('es');
  const [searchText, setSearchText] = React.useState('');
  const debouncedSearchText = useDebouncedMemo(() => searchText, 300, [searchText]);
  const [sortBy, setSortBy] = React.useState<SortOption>(DEFAULT_SORT);
  const [filterBy, setFilterBy] = React.useState<FilterOption>(DEFAULT_FILTER);
  const [audienceFilter, setTagFilter] = React.useState<AudienceFilterOption>('all');
  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 50;

  const [loadedTranslations, setLoadedTranslations] = React.useState<TranslationCache>({});
  const [languageErrors, setLanguageErrors] = React.useState<Partial<Record<SupportedLanguage, string>>>({});

  const [viewingKey, setViewingKey] = React.useState<string | null>(null);
  const [updatedHumanTranslationValue, setUpdatedHumanTranslationValue] = React.useState('');
  const [pendingAudience, setPendingAudience] = React.useState<Audience | null>(null);

  const [editingTranslations, setEditingTranslations] = React.useState<TranslationEdits>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Failed to load saved edits:', err);
      return {};
    }
  });
  const editingTranslationsRef = React.useRef(editingTranslations);

  const [audienceOverrides, setAudienceOverrides] = React.useState<AudienceOverrides>(() => {
    try {
      const saved = localStorage.getItem(AUDIENCE_OVERRIDES_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch (err) {
      console.error('Failed to load saved audience overrides:', err);
      return {};
    }
  });
  const audienceOverridesRef = React.useRef(audienceOverrides);

  const [showSaveModal, setShowSaveModal] = React.useState(false);
  const [showExportModal, setShowExportModal] = React.useState(false);
  const [showImportModal, setShowImportModal] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    editingTranslationsRef.current = editingTranslations;
    try {
      if (Object.keys(editingTranslations).length > 0) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(editingTranslations));
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      console.error('Failed to save edits to localStorage:', err);
    }
  }, [editingTranslations]);

  React.useEffect(() => {
    audienceOverridesRef.current = audienceOverrides;
    try {
      if (Object.keys(audienceOverrides).length > 0) {
        localStorage.setItem(AUDIENCE_OVERRIDES_KEY, JSON.stringify(audienceOverrides));
      } else {
        localStorage.removeItem(AUDIENCE_OVERRIDES_KEY);
      }
    } catch (err) {
      console.error('Failed to save audience overrides to localStorage:', err);
    }
  }, [audienceOverrides]);

  // Load translations for a language
  const loadLanguage = React.useCallback(async (lang: SupportedLanguage) => {
    // Skip if already loaded
    if (loadedTranslations[lang]) return;

    setLanguageErrors(prev => {
      const updated = { ...prev };
      delete updated[lang];
      return updated;
    });

    try {
      const response = await getTranslations({ lang });
      setLoadedTranslations(prev => ({
        ...prev,
        [lang]: response as TranslationsResponse
      }));
    } catch (err) {
      console.error(`Failed to fetch ${lang} translations:`, err);
      setLanguageErrors(prev => ({
        ...prev,
        [lang]: `Failed to load ${LanguageNames[lang]}`
      }));
    }
  }, [loadedTranslations, getTranslations]);

  const refreshLanguage = React.useCallback(async (lang: SupportedLanguage) => {
    try {
      const response = await getTranslations({ lang });
      setLoadedTranslations(prev => ({ ...prev, [lang]: response as TranslationsResponse }));
    } catch (err) {
      console.error(`Failed to refresh ${lang} translations:`, err);
    }
  }, [getTranslations]);

  // Load base language when it changes
  React.useEffect(() => {
    if (baseLang) {
      loadLanguage(baseLang);
    }
  }, [baseLang, loadLanguage]);

  // Load translation language when it changes
  React.useEffect(() => {
    loadLanguage(translationLang);
  }, [translationLang, loadLanguage]);

  const baseTranslationsMap = React.useMemo(() =>
    baseLang ? createTranslationMap(loadedTranslations[baseLang]?.translations) : {},
  [loadedTranslations, baseLang]
  );

  const translationMap = React.useMemo(() =>
    createTranslationMap(loadedTranslations[translationLang]?.translations),
  [loadedTranslations, translationLang]
  );

  // Count total unsaved changes across all languages + audience overrides
  const totalUnsavedChanges = React.useMemo(() => {
    const translationEditsCount = Object.values(editingTranslations).reduce(
      (sum, edits) => sum + Object.keys(edits).length,
      0
    );
    return translationEditsCount + Object.keys(audienceOverrides).length;
  }, [editingTranslations, audienceOverrides]);

  // Returns only edits where the new value actually differs from the saved human_translation
  const getChangedEdits = React.useCallback((lang: SupportedLanguage, edits: Record<string, string>) => {
    const langTranslations = loadedTranslations[lang]?.translations ?? [];
    const humanMap = new Map(langTranslations.map(t => [t.key, t.human_translation]));
    return Object.fromEntries(
      Object.entries(edits).filter(([key, value]) => value !== humanMap.get(key))
    );
  }, [loadedTranslations]);

  // Save changes to DynamoDB for a single language
  const handleSaveLanguage = React.useCallback(async (lang: SupportedLanguage) => {
    const rawEdits = editingTranslationsRef.current[lang];
    if (!rawEdits || Object.keys(rawEdits).length === 0) return;

    const editsToSave = getChangedEdits(lang, rawEdits);
    if (Object.keys(editsToSave).length === 0) return;

    setIsSaving(true);
    try {
      await Promise.all(
        Object.entries(editsToSave).map(([key, human_translation]) =>
          updateHumanTranslation({ lang, key, human_translation })
        )
      );

      // Clear all local edits for this language
      setEditingTranslations(prev => {
        const updated = { ...prev };
        delete updated[lang];
        return updated;
      });

      const response = await getTranslations({ lang });
      setLoadedTranslations(prev => ({
        ...prev,
        [lang]: response as TranslationsResponse
      }));
    } catch (err) {
      console.error(`Failed to save ${lang} translations:`, err);
      alert(`Failed to save some ${LanguageNames[lang]} translations. Please try again.`);
    } finally {
      setIsSaving(false);
    }
  }, [updateHumanTranslation, getTranslations, getChangedEdits]);

  // Save audience overrides only
  const handleSaveAudienceOverrides = React.useCallback(async () => {
    const overridesToSave = { ...audienceOverridesRef.current };
    if (Object.keys(overridesToSave).length === 0) return;

    setIsSaving(true);
    try {
      await Promise.all(
        Object.entries(overridesToSave).map(([key, audience]) =>
          updateAudience({ key, audience })
        )
      );

      setAudienceOverrides(prev => {
        const updated = { ...prev };
        Object.keys(overridesToSave).forEach(key => delete updated[key]);
        return updated;
      });

      const response = await getTranslations({ lang: translationLang });
      setLoadedTranslations(prev => ({
        ...prev,
        [translationLang]: response as TranslationsResponse
      }));
    } catch (err) {
      console.error('Failed to save audience overrides:', err);
      alert('Failed to save audience overrides. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [updateAudience, getTranslations, translationLang]);

  // Save all changes across all languages to DynamoDB
  const handleSaveAll = React.useCallback(async () => {
    setIsSaving(true);
    try {
      const allUpdates = Object.entries(editingTranslationsRef.current).flatMap(([lang, edits]) => {
        const changed = getChangedEdits(lang as SupportedLanguage, edits);
        return Object.entries(changed).map(([key, human_translation]) => ({
          lang,
          key,
          human_translation
        }));
      });

      const audienceOverridesToSave = { ...audienceOverridesRef.current };

      await Promise.all([
        ...allUpdates.map(update => updateHumanTranslation(update)),
        ...Object.entries(audienceOverridesToSave).map(([key, audience]) =>
          updateAudience({ key, audience })
        ),
      ]);

      // Clear all local edits
      setEditingTranslations({});
      setAudienceOverrides({});

      const affectedLanguages = Array.from(new Set(allUpdates.map(u => u.lang as SupportedLanguage)));
      if (Object.keys(audienceOverridesToSave).length > 0 && !affectedLanguages.includes(translationLang)) {
        affectedLanguages.push(translationLang);
      }
      await Promise.all(
        affectedLanguages.map(async (lang) => {
          const response = await getTranslations({ lang });
          setLoadedTranslations(prev => ({
            ...prev,
            [lang]: response as TranslationsResponse
          }));
        })
      );

      setShowSaveModal(false);
    } catch (err) {
      console.error('Failed to save all translations:', err);
      alert('Failed to save some translations. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [updateHumanTranslation, updateAudience, getTranslations, translationLang, getChangedEdits]);

  const handleModalClose = React.useCallback(() => {
    setViewingKey(null);
    setPendingAudience(null);
  }, []);

  const handleModalSave = React.useCallback(() => {
    if (!viewingKey) return;

    // Only record a translation edit if the user actually typed something
    if (updatedHumanTranslationValue.trim() !== '') {
      setEditingTranslations(prev => ({
        ...prev,
        [translationLang]: {
          ...(prev[translationLang] || {}),
          [viewingKey]: updatedHumanTranslationValue
        }
      }));
    }

    // Save audience override if it differs from the current stored audience
    const existingAudience = translationMap[viewingKey]?.audience;
    if (pendingAudience !== null && pendingAudience !== existingAudience) {
      setAudienceOverrides(prev => ({ ...prev, [viewingKey]: pendingAudience }));
    } else {
      // Clear any pending override if it was reset back to current value
      setAudienceOverrides(prev => {
        const updated = { ...prev };
        delete updated[viewingKey];
        return updated;
      });
    }

    setViewingKey(null);
    setPendingAudience(null);
  }, [viewingKey, updatedHumanTranslationValue, translationLang, pendingAudience, translationMap]);

  const handleDiscardLanguage = React.useCallback((lang: SupportedLanguage) => {
    setEditingTranslations(prev => {
      const updated = { ...prev };
      delete updated[lang];
      return updated;
    });
  }, []);

  const handleDiscardAudienceOverrides = React.useCallback(() => {
    setAudienceOverrides({});
  }, []);

  const handleDiscardAll = React.useCallback(() => {
    setEditingTranslations({});
    setAudienceOverrides({});
  }, []);

  const handleRowClick = React.useCallback((key: string) => {
    setViewingKey(key);
    // Only pre-fill if there's already a local edit in progress; otherwise start blank
    const localEdit = editingTranslationsRef.current[translationLang]?.[key];
    setUpdatedHumanTranslationValue(localEdit ?? '');
    // Restore any pending audience override for this key
    setPendingAudience(audienceOverridesRef.current[key] ?? null);
  }, [translationLang]);

  const handleApproveAI = React.useCallback((key: string, aiTranslation: string) => {
    setEditingTranslations(prev => ({
      ...prev,
      [translationLang]: {
        ...(prev[translationLang] || {}),
        [key]: aiTranslation
      }
    }));
  }, [translationLang]);

  const filteredKeys = React.useMemo(() => {
    let keys = Object.keys(translationMap);
    if (keys.length === 0) return [];

    if (filterBy === 'needs_review') {
      keys = keys.filter(key => translationMap[key]?.needs_review);
    } else if (filterBy === 'human_reviewed') {
      keys = keys.filter(key => translationMap[key]?.human_translation !== undefined);
    }

    if (audienceFilter !== 'all') {
      keys = keys.filter(key => translationMap[key]?.audience === audienceFilter);
    }

    if (debouncedSearchText) {
      const searchLower = debouncedSearchText.toLowerCase();
      keys = keys.filter(key => {
        const base = baseTranslationsMap[key];
        const trans = translationMap[key];
        return [
          key,
          base?.ai_translation,
          base?.human_translation,
          trans?.ai_translation,
          trans?.human_translation,
        ].some(text => text?.toLowerCase().includes(searchLower));
      });
    }

    return keys.sort((a, b) => {
      const transA = translationMap[a];
      const transB = translationMap[b];

      if (sortBy === 'last_modified') {
        const dateA = transA?.last_modified ? new Date(transA.last_modified).getTime() : 0;
        const dateB = transB?.last_modified ? new Date(transB.last_modified).getTime() : 0;
        return dateB - dateA;
      } else if (sortBy === 'needs_review') {
        if (transA?.needs_review && !transB?.needs_review) return -1;
        if (!transA?.needs_review && transB?.needs_review) return 1;
      } else if (sortBy === 'applicant_facing_first') {
        const aFacing = transA?.audience === APPLICANT_FACING;
        const bFacing = transB?.audience === APPLICANT_FACING;
        if (aFacing && !bFacing) return -1;
        if (!aFacing && bFacing) return 1;
      }
      return a.localeCompare(b);
    });
  }, [translationMap, baseTranslationsMap, debouncedSearchText, filterBy, audienceFilter, sortBy]);

  const paginatedKeys = React.useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredKeys.slice(startIndex, endIndex);
  }, [filteredKeys, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredKeys.length / itemsPerPage);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [filterBy, audienceFilter, sortBy, debouncedSearchText, translationLang]);

  return (
    <div className="p-4">
      <div className="flex justify-end gap-2 mb-4">
        <Button variant="secondary" onClick={() => setShowImportModal(true)}>
          Import CSV
        </Button>
        <Button variant="secondary" onClick={() => setShowExportModal(true)}>
          Export CSV
        </Button>
      </div>

      <TranslationControls
        baseLang={baseLang}
        setBaseLang={setBaseLang}
        translationLang={translationLang}
        setTranslationLang={setTranslationLang}
        searchText={searchText}
        setSearchText={setSearchText}
        sortBy={sortBy}
        setSortBy={setSortBy}
        filterBy={filterBy}
        setFilterBy={setFilterBy}
        audienceFilter={audienceFilter}
        setTagFilter={setTagFilter}
        baseError={baseLang ? (languageErrors[baseLang] || null) : null}
        translationError={languageErrors[translationLang] || null}
        translationStats={loadedTranslations[translationLang]?.stats || null}
      />

      {totalUnsavedChanges > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4 flex items-center justify-between">
          <div>
            <span className="font-medium">{totalUnsavedChanges}</span> unsaved change{totalUnsavedChanges !== 1 ? 's' : ''}
          </div>
          <Button
            variant="primary"
            onClick={() => setShowSaveModal(true)}
          >
            Review & Save
          </Button>
        </div>
      )}

      <TranslationsTable
        baseLang={baseLang}
        translationLang={translationLang}
        filteredKeys={paginatedKeys}
        baseTranslationsMap={baseTranslationsMap}
        translationMap={translationMap}
        editingTranslations={editingTranslations}
        translationError={languageErrors[translationLang] || null}
        onRowClick={handleRowClick}
        onApproveAI={handleApproveAI}
      />

      {/* Pagination Controls */}
      {filteredKeys.length > itemsPerPage && (
        <div className="flex items-center justify-between mt-4 px-4">
          <div className="text-sm text-gray-600">
            Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredKeys.length)} of {filteredKeys.length}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      <EditTranslationModal
        viewingKey={viewingKey}
        baseLang={baseLang}
        translationLang={translationLang}
        baseTranslationsMap={baseTranslationsMap}
        translationMap={translationMap}
        updatedHumanTranslationValue={updatedHumanTranslationValue}
        pendingAudience={pendingAudience}
        onClose={handleModalClose}
        onUpdateValue={setUpdatedHumanTranslationValue}
        onUpdateAudience={setPendingAudience}
        onSave={handleModalSave}
      />

      {showImportModal && (
        <ImportTranslationsModal
          onClose={() => setShowImportModal(false)}
          loadedTranslations={loadedTranslations}
          onImportComplete={refreshLanguage}
          getTranslations={getTranslations}
        />
      )}

      {showExportModal && (
        <ExportTranslationsModal
          onClose={() => setShowExportModal(false)}
          loadedTranslations={loadedTranslations}
          getTranslations={getTranslations}
        />
      )}

      {showSaveModal && (
        <SaveAllModal
          editingTranslations={editingTranslations}
          audienceOverrides={audienceOverrides}
          allTranslationsData={loadedTranslations}
          onClose={() => setShowSaveModal(false)}
          onSaveLanguage={handleSaveLanguage}
          onSaveAll={handleSaveAll}
          onSaveAudienceOverrides={handleSaveAudienceOverrides}
          onDiscardAudienceOverrides={handleDiscardAudienceOverrides}
          onDiscardLanguage={handleDiscardLanguage}
          onDiscardAll={handleDiscardAll}
          isSaving={isSaving}
        />
      )}
    </div>
  );
};
